import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Ticket, Clock, Users, AlertCircle, ChevronRight, Bell, BookOpen, PhoneIncoming, PhoneOutgoing, MessageSquare, FileText, Mail, Check } from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState({ open: 0, in_progress: 0, waiting: 0, followup: 0, customers: 0 });
  const [recentTickets, setRecentTickets] = useState([]);
  const [followupToday, setFollowupToday] = useState([]);
  const [journalFollowupToday, setJournalFollowupToday] = useState([]);
  const [journalRecent, setJournalRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [ticketsRes, customersRes, journalFollowupRes] = await Promise.all([
          api.get('/tickets'),
          api.get('/customers'),
          api.get('/journal/dashboard'),
        ]);
        const tickets = ticketsRes.data;
        const customers = customersRes.data;
        const todayStr = new Date().toISOString().slice(0, 10);
        const followups = tickets.filter(t => t.status === 'followup');
        const todayFollowups = followups.filter(t => {
          if (!t.followup_at) return false;
          return t.followup_at.slice(0, 10) <= todayStr;
        });
        setStats({
          open: tickets.filter(t => t.status === 'open').length,
          in_progress: tickets.filter(t => t.status === 'in_progress').length,
          waiting: tickets.filter(t => t.status === 'waiting').length,
          followup: followups.length,
          customers: customers.filter(c => c.is_active).length,
        });
        setRecentTickets(tickets.filter(t => t.status !== 'closed').slice(0, 8));
        setFollowupToday(todayFollowups);
        setJournalFollowupToday(journalFollowupRes.data.followups || []);
        setJournalRecent(journalFollowupRes.data.recent || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const markFollowupDone = async (id, e) => {
    e.stopPropagation();
    try {
      await api.patch('/journal/' + id + '/followup-done');
      setJournalFollowupToday(prev => prev.filter(entry => entry.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const statusLabel = {
    open: 'Offen',
    in_progress: 'In Bearbeitung',
    waiting: 'Wartend',
    followup: 'Wiedervorlage',
    closed: 'Geschlossen',
  };

  const statusColor = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    waiting: 'bg-orange-100 text-orange-700',
    followup: 'bg-purple-100 text-purple-700',
    closed: 'bg-gray-100 text-gray-500',
  };

  const statCards = [
    { label: 'Offen', value: stats.open, icon: AlertCircle, color: 'text-blue-500', bg: 'bg-blue-50', action: () => navigate('/tickets?status=open') },
    { label: 'In Bearbeitung', value: stats.in_progress, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50', action: () => navigate('/tickets?status=in_progress') },
    { label: 'Wartend', value: stats.waiting, icon: Ticket, color: 'text-orange-500', bg: 'bg-orange-50', action: () => navigate('/tickets?status=waiting') },
    { label: 'Wiedervorlage', value: stats.followup, icon: Bell, color: 'text-purple-500', bg: 'bg-purple-50', action: () => navigate('/tickets?status=followup') },
    { label: 'Kunden', value: stats.customers, icon: Users, color: 'text-teal-500', bg: 'bg-teal-50', action: () => navigate('/customers') },
  ];

  if (loading) return <div className="text-gray-400 text-sm">Laden...</div>;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <p className="text-gray-500 text-sm mt-1">Uebersicht aller aktiven Vorgaenge</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {statCards.map(({ label, value, icon: Icon, color, bg, action }) => (
          <div key={label}
            onClick={action}
            className="bg-white rounded-xl border border-gray-100 px-3 py-2.5 shadow-sm hover:shadow-md transition-all cursor-pointer flex items-center gap-3 min-w-0"
          >
            <div className={'w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ' + bg}>
              <Icon size={16} className={color} />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
              <p className="text-xs text-gray-500 mt-0.5 truncate">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {followupToday.length > 0 && (
        <div className="bg-white rounded-2xl border border-purple-100 shadow-sm mb-6">
          <div className="p-5 border-b border-purple-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-purple-500" />
              <h3 className="font-semibold text-gray-900">Wiedervorlagen heute</h3>
              <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-0.5 rounded-full">{followupToday.length}</span>
            </div>
            <button onClick={() => navigate('/tickets?status=followup')} className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1">
              Alle anzeigen <ChevronRight size={16} />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {followupToday.map(ticket => (
              <div key={ticket.id}
                onClick={() => navigate('/tickets/' + ticket.id)}
                className="px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between cursor-pointer hover:bg-purple-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{ticket.ticket_number}{(ticket.customer_name || ticket.supplier_name) ? ' · ' + (ticket.supplier_name || ticket.customer_name) : ''}</p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">{ticket.title}</p>
                </div>
                <div className="ml-4 shrink-0">
                  <span className="text-xs text-gray-400">
                    {ticket.followup_at ? new Date(ticket.followup_at).toLocaleDateString('de-DE') : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {journalFollowupToday.length > 0 && (
        <div className="bg-white rounded-2xl border border-teal-100 shadow-sm mb-6">
          <div className="p-5 border-b border-teal-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-teal-500" />
              <h3 className="font-semibold text-gray-900">Journal-Wiedervorlagen heute</h3>
              <span className="bg-teal-100 text-teal-700 text-xs font-medium px-2 py-0.5 rounded-full">{journalFollowupToday.length}</span>
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {journalFollowupToday.map(entry => (
              <div key={entry.id}
                onClick={() => navigate('/journal')}
                className="px-4 sm:px-5 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 cursor-pointer hover:bg-teal-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{entry.customer_name}</p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">{entry.note}</p>
                </div>
                <div className="sm:ml-4 shrink-0 flex items-center justify-between sm:justify-end gap-3">
                  <span className="text-xs text-gray-400">
                    {entry.followup_at ? new Date(entry.followup_at).toLocaleDateString('de-DE') : ''}
                  </span>
                  <button
                    onClick={(e) => markFollowupDone(entry.id, e)}
                    className="flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-white hover:bg-teal-500 border border-teal-200 hover:border-teal-500 px-2.5 py-1 rounded-lg transition-colors"
                  >
                    <Check size={14} /> Erledigt
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-primary-500" />
            <h3 className="font-semibold text-gray-900">Letzte Journal-Eintr&#228;ge</h3>
          </div>
          <button onClick={() => navigate('/journal')} className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1">
            Alle anzeigen <ChevronRight size={16} />
          </button>
        </div>
        <div className="divide-y divide-gray-50">
          {journalRecent.length === 0 && (
            <p className="text-sm text-gray-400 p-5">Keine Eintr&#228;ge vorhanden</p>
          )}
          {journalRecent.map(entry => (
            <div key={entry.id}
              onClick={() => navigate('/journal')}
              className="px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{entry.customer_name}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{entry.note}</p>
              </div>
              <div className="ml-4 shrink-0 text-xs text-gray-400">
                {new Date(entry.created_at).toLocaleDateString('de-DE')}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Letzte Tickets</h3>
          <button onClick={() => navigate('/tickets')} className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1">
            Alle anzeigen <ChevronRight size={16} />
          </button>
        </div>
        <div className="divide-y divide-gray-50">
          {recentTickets.length === 0 && (
            <p className="text-sm text-gray-400 p-5">Keine Tickets vorhanden</p>
          )}
          {recentTickets.map(ticket => (
            <div key={ticket.id}
              onClick={() => navigate('/tickets/' + ticket.id)}
              className={'px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between cursor-pointer transition-colors border-l-2 ' + (ticket.supplier_id ? 'bg-amber-50 hover:bg-amber-100 border-amber-300' : 'hover:bg-gray-50 border-transparent')}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{ticket.ticket_number} · {ticket.title}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{ticket.supplier_name || ticket.customer_name || ''}</p>
              </div>
              <div className="flex items-center gap-3 ml-4 shrink-0">
                <span className="text-xs text-gray-300 hidden md:block">
                  {new Date(ticket.created_at).toLocaleDateString('de-DE')}
                </span>
                <span className={'text-xs font-medium px-2.5 py-1 rounded-full ' + (statusColor[ticket.status] || '')}>
                  {statusLabel[ticket.status]}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
